import { format } from "date-fns";
import { FLIGHT_DATES } from 'state/constants';


export function countLoadFactorOverall(
    flights, 
    dataType = 'actual', 
    minimumPax = 0, 
    chartType
) {
    if (!flights) return null;
    
    const obj = flights.reduce((acc, flight) => {
        const expectedNoOfPax = Number(flight.expectedNoOfPax) || 0;
        const actualNoOfPax = Number(flight.noOfPax) || 0;
        const noshowPax = expectedNoOfPax - actualNoOfPax;
        const capacity = Number(flight.capacity) || 0;

        let pax;
        switch (dataType) {
            case 'expected':
                pax = expectedNoOfPax;
                break;
            case 'actual':
                pax = actualNoOfPax;
                break;
            default:
                pax = actualNoOfPax;
        }

        if (pax <= minimumPax || capacity === 0) return acc;

        acc.flightTotal += 1;
        acc.expectedPaxTotal += Math.min(expectedNoOfPax, capacity);
        acc.actualPaxTotal += Math.min(actualNoOfPax, capacity);
        acc.noshowPaxTotal += Math.max(noshowPax, 0);
        acc.paxTotal += Math.min(pax, capacity);
        acc.capacityTotal += capacity;
        acc.vacancyTotal += Math.max(capacity - pax, 0);

        return acc;
    }, { flightTotal: 0, 
        expectedPaxTotal: 0, 
        actualPaxTotal: 0, 
        noshowPaxTotal: 0, 
        paxTotal: 0, 
        capacityTotal: 0, 
        vacancyTotal: 0 
    });

        
    if (Object.keys(obj).length === 0 || !obj.paxTotal) return [];
    
    const barChartData = dataType === 'noshow' ? [
        { x: 'Expected', y: obj.expectedPaxTotal },
        { x: 'Noshow', y: obj.noshowPaxTotal },
        { x: 'Actual', y: obj.actualPaxTotal }
    ] : [
        { x: 'Capacity', y: obj.capacityTotal },
        { x: 'Pax', y: obj.paxTotal },
        { x: 'Vacancy', y: obj.vacancyTotal }
    ];
    
    switch (chartType) {
        case 'bar':
        case 'column':
            return barChartData;
        case 'pie':            
            const pieChartData = Object.entries(dataType === 'noshow' ? {
                "Noshow": obj.noshowPaxTotal,
                "Actual": obj.actualPaxTotal
            } : {
                "Pax": obj.paxTotal,
                "Vacancy": obj.vacancyTotal
            }).map(([key, value]) => ({
                id: key,
                label: key,
                value
            }));

            return pieChartData;        
        default:
            return barChartData;            
    }
}

export function countLoadFactorByField(
    flights, 
    groupBy, 
    dataType = 'actual', 
    minimumPax = 0, 
    chartType
) {   
    if (!flights) return null;

    const objValues = flights.reduce((acc, flight) => {
        const key = flight[groupBy];
        if (!key) return acc;

        const expectedNoOfPax = Number(flight.expectedNoOfPax) || 0;
        const actualNoOfPax = Number(flight.noOfPax) || 0;
        const noshowPax = Math.max(expectedNoOfPax - actualNoOfPax, 0);
        const capacity = Number(flight.capacity) || 0;

        let pax;
        switch (dataType) {
            case 'expected': pax = expectedNoOfPax; break;
            case 'actual': pax = actualNoOfPax; break;
            case 'noshow': pax = noshowPax; break;
            default: pax = actualNoOfPax;
        }

        if (pax <= minimumPax || capacity === 0) return acc;

        if (!acc[key]) {
            acc[key] = { 
                flightTotal: 0, 
                expectedPaxTotal: 0, 
                actualPaxTotal: 0, 
                noshowPaxTotal: 0, 
                paxTotal: 0, 
                capacityTotal: 0, 
                vacancyTotal: 0
            };
        }        

        acc[key].flightTotal += 1;
        acc[key].expectedPaxTotal += Math.min(expectedNoOfPax, capacity);
        acc[key].actualPaxTotal += Math.min(actualNoOfPax, capacity);
        acc[key].noshowPaxTotal += noshowPax;
        acc[key].paxTotal += Math.min(pax, capacity);
        acc[key].capacityTotal += capacity;
        acc[key].vacancyTotal += Math.max(capacity - pax, 0);

        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];
   
    const { totalNoshows, totalPaxes, totalCapacities, totalExpected } = Object.values(objValues).reduce(
        (acc, { noshowPaxTotal, paxTotal, capacityTotal, expectedPaxTotal }) => {
            acc.totalNoshows += noshowPaxTotal;
            acc.totalPaxes += paxTotal;
            acc.totalCapacities += capacityTotal;
            acc.totalExpected += expectedPaxTotal;
            return acc;
        }, 
        { totalNoshows: 0, totalPaxes: 0, totalCapacities: 0, totalExpected: 0 }
    );

    // Average theo dataType
    let averageLF;
    if (dataType === 'noshow') {
        averageLF = totalExpected > 0 
            ? parseFloat(((totalNoshows / totalExpected) * 100).toFixed(1)) 
            : 0;
    } else {
        averageLF = totalCapacities > 0 
            ? parseFloat(((totalPaxes / totalCapacities) * 100).toFixed(1)) 
            : 0;
    }

    const dateFields = new Set(FLIGHT_DATES);

    // Convert accumulated data theo dataType
    const arrValues = Object.entries(objValues)
        .map(([key, { paxTotal, capacityTotal, noshowPaxTotal, expectedPaxTotal }]) => {
            let ratio;
            if (dataType === 'noshow') {
                ratio = expectedPaxTotal > 0 
                    ? (noshowPaxTotal / expectedPaxTotal) * 100 
                    : 0;
            } else {
                ratio = capacityTotal > 0 
                    ? (paxTotal / capacityTotal) * 100 
                    : 0;
            }
            return {
                x: key,
                y: Math.min(100, parseFloat(ratio.toFixed(1)))
            };
        })
        .sort((a, b) => dateFields.has(groupBy) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        );

    const arrAverage = arrValues.map(({ x }) => ({ x, y: averageLF }));

    const barLineChartData = [        
        { id: dataType === 'noshow' ? 'Noshow %' : 'Load Factor', color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];

    const arrTimeRangeData = Object.entries(objValues).map(([key, value]) => {
        let ratio;
        if (dataType === 'noshow') {
            ratio = value.expectedPaxTotal > 0 
                ? (value.noshowPaxTotal / value.expectedPaxTotal) * 100 
                : 0;
        } else {
            ratio = value.capacityTotal > 0 
                ? (value.paxTotal / value.capacityTotal) * 100 
                : 0;
        }
        return {
            value: Math.min(100, parseFloat(ratio.toFixed(2))),
            day: dateFields.has(groupBy) ? format(new Date(key), 'yyyy-MM-dd') : null,
        };
    });

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':            
            return Object.entries(objValues).map(([key, value]) => ({
                id: key,
                label: key,
                value: dataType === 'noshow' ? value.noshowPaxTotal : value.paxTotal,
            }));     
        case 'timerange':
            return arrTimeRangeData; 
        case 'barline':  
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

export function countPaxByField(
    flights, 
    groupBy, 
    dataType = 'actual', 
    minimumPax = 0, 
    chartType
) {   
    if (!flights) return null;

    const objValues = flights.reduce((acc, flight) => {
        const key = flight[groupBy];
        if (!key) return acc;

        const expectedNoOfPax = Number(flight.expectedNoOfPax) || 0;
        const actualNoOfPax = Number(flight.noOfPax) || 0;
        const noshowPax = Math.max(expectedNoOfPax - actualNoOfPax, 0);
        const capacity = Number(flight.capacity) || 0;

        let pax;
        switch (dataType) {
            case 'expected': pax = expectedNoOfPax; break;
            case 'actual': pax = actualNoOfPax; break;
            case 'noshow': pax = noshowPax; break;
            default: pax = actualNoOfPax;
        }

        if (pax <= minimumPax || capacity === 0) return acc;

        if (!acc[key]) {
            acc[key] = { 
                totalFlt: 0, 
                totalPax: 0,
            };
        }

        acc[key].totalFlt += 1;
        acc[key].totalPax += (dataType === 'noshow') 
            ? pax // noshow không bị giới hạn bởi capacity
            : Math.min(pax, capacity);  

        return acc;
    }, {});
    
    const sumUpTotalPax = Object.values(objValues).reduce((sum, obj) => sum + obj?.totalPax, 0);

    if (Object.keys(objValues).length === 0 || !sumUpTotalPax) return [];

    const dateFields = new Set(FLIGHT_DATES);

    // Convert accumulated data to the required format
    const arrValues = Object.entries(objValues)
        .map(([key, { totalPax }]) => ({
            x: key,
            y: totalPax,
        }))
        .sort((a, b) => dateFields.has(groupBy) 
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x)
        );

    const totalPaxes = Object.values(objValues).reduce((acc, { totalPax }) => acc + totalPax, 0);
    const average = Object.keys(objValues).length > 0 
        ? parseFloat((totalPaxes / Object.keys(objValues).length).toFixed(0)) 
        : 0;

    const arrAverage = arrValues.map(({ x }) => ({ x, y: average }));

    const barLineChartData = [        
        { id: dataType === 'noshow' ? 'No-Show Pax' : 'Pax', color: 'hsl(120, 70%, 50%)', data: arrValues },        
        { id: 'Average', color: 'hsl(200, 70%, 50%)', data: arrAverage },
    ];

    const arrTimeRangeData = Object.entries(objValues).map(([key, value]) => ({
        value: value.totalPax || 0,
        day: dateFields.has(groupBy) ? format(new Date(key), 'yyyy-MM-dd') : null,
    }));

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':            
            return Object.entries(objValues).map(([key, value]) => ({
                id: key,
                label: key,
                value: value.totalPax,
            }));      
        case 'timerange':
            return arrTimeRangeData; 
        case 'barline':  
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

export function countLoadFactorBy2Fields(
    flights, 
    field1, 
    field2, 
    dataType = 'actual', 
    minimumPax = 0
) {   
    if (!flights) return null;
    
    const objValues = flights.reduce((acc, flight) => {
        const key = field2 
            ? `${flight[field1]}|${flight[field2]}`
            : `${flight[field1]}|Value`;

        const expectedNoOfPax = Number(flight.expectedNoOfPax) || 0;
        const actualNoOfPax = Number(flight.noOfPax) || 0;
        const noshowPax = Math.max(expectedNoOfPax - actualNoOfPax, 0);
        const capacity = Number(flight.capacity) || 0;

        let pax;
        switch (dataType) {
            case 'expected': pax = expectedNoOfPax; break;
            case 'actual': pax = actualNoOfPax; break;
            case 'noshow': pax = noshowPax; break;
            default: pax = actualNoOfPax;
        }

        if (pax <= minimumPax || capacity === 0) return acc;

        if (!acc[key]) {
            acc[key] = { 
                totalFlt: 0, 
                totalPax: 0,
                totalCapacity: 0,
                totalVacancy: 0,
                flights: [],
            };
        }

        acc[key].totalFlt += 1;
        acc[key].totalCapacity += capacity;

        if (dataType === 'noshow') {
            // với noshow: pax không giới hạn bởi capacity, vacancy không áp dụng
            acc[key].totalPax += pax;
        } else {
            acc[key].totalPax += Math.min(pax, capacity);
            acc[key].totalVacancy += Math.max(capacity - pax, 0);
        }

        acc[key].flights.push({
            flightNo: flight.flightNo,
            flightDate: flight.flightDate,
            flightDateLocalBase: flight.flightDateLocalBase,
            sector: flight.sector,
            etd: flight.etd,
            eta: flight.eta,
            atd: flight.atd,
            ata: flight.ata,
            etdLocalBase: flight.etdLocalBase,
            etaLocalBase: flight.etaLocalBase,
            atdLocalBase: flight.atdLocalBase,
            ataLocalBase: flight.ataLocalBase,
            expectedNoOfPax,
            actualNoOfPax,
            pax,
            capacity,
            loadFactor: (Math.min(100, pax / capacity * 100)).toFixed(1),
        });
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const arrValues = Object.entries(objValues)
        .map(([key, { totalPax, totalCapacity, flights }]) => ({
            key,
            lf: dataType === 'noshow'
                ? (totalPax > 0 && totalCapacity > 0) 
                    ? parseFloat(((totalPax / totalCapacity) * 100).toFixed(1)) 
                    : 0
                : totalCapacity > 0 
                    ? Math.min(100, parseFloat((totalPax / totalCapacity * 100).toFixed(1))) 
                    : 0,
            totalPax,
            totalCapacity,
            flights,
        }))
        .sort((a, b) => {
            const [partA] = a.key.split("|");
            const [partB] = b.key.split("|");
            const dateA = Date.parse(partA);
            const dateB = Date.parse(partB);

            if (!isNaN(dateA) && !isNaN(dateB)) {
                return dateA - dateB;
            }
            return a.key.localeCompare(b.key);
        });

    return arrValues;
}

